/**
 * SingleAssetLooping Strategy
 */
import { Decimal } from 'decimal.js';
import { BaseStrategy } from './strategy.js';
import { ALPHALEND_LENDING_PROTOCOL_ID, CLOCK_PACKAGE_ID, DISTRIBUTOR_OBJECT_ID, GLOBAL_CONFIGS, POOLS, VERSIONS, } from '../utils/constants.js';
/**
 * SingleAssetLooping Strategy for leveraged positions on single assets
 */
export class SingleAssetLoopingStrategy extends BaseStrategy {
    poolLabel;
    poolObject;
    investorObject;
    receiptObjects = [];
    context;
    constructor(poolLabel, poolObject, investorObject, context) {
        super();
        this.poolLabel = poolLabel;
        this.poolObject = this.parsePoolObject(poolObject);
        this.investorObject = this.parseInvestorObject(investorObject);
        this.context = context;
    }
    getPoolLabel() {
        return this.poolLabel;
    }
    getOtherAmount(_amount, _isAmountA) {
        throw new Error('getOtherAmount is not supported for single-asset SingleAssetLooping strategy');
    }
    updateReceipts(receipts) {
        this.receiptObjects = this.parseReceiptObjects(receipts);
    }
    /**
     * Get alpha mining data including pool and receipt information
     */
    getAlphaMiningData() {
        const receipt = this.receiptObjects.length > 0 ? this.receiptObjects[0] : null;
        return {
            poolId: this.poolLabel.poolId,
            accRewardsPerXtoken: this.poolObject.accRewardsPerXtoken,
            xTokenSupply: this.poolObject.xTokenSupply,
            receipt: receipt
                ? {
                    lastAccRewardPerXtoken: receipt.lastAccRewardPerXtoken,
                    pendingRewards: receipt.pendingRewards,
                    xTokenBalance: receipt.xTokenBalance,
                }
                : null,
        };
    }
    /**
     * Get the exchange rate for xtoken to underlying token ratio
     * Adjusted for current debt-to-supply ratio in leveraged single-asset position
     */
    exchangeRate() {
        const currentDebtToSupplyRatio = new Decimal(this.investorObject.currentDebtToSupplyRatio);
        const tokensInvested = new Decimal(this.investorObject.tokensDeposited).mul(new Decimal(1).minus(currentDebtToSupplyRatio.div(new Decimal(10).pow(20))));
        const xtokenSupply = new Decimal(this.poolObject.xTokenSupply);
        if (xtokenSupply.isZero()) {
            return new Decimal(1); // Default exchange rate when no tokens are supplied
        }
        return tokensInvested.div(xtokenSupply);
    }
    /**
     * Get comprehensive pool data including TVL and APR information
     */
    async getData() {
        const [alphafi, parent, apr] = await Promise.all([
            this.getTvl(),
            this.getParentTvl(),
            this.context.getAprData(this.poolLabel.poolId),
        ]);
        return {
            poolId: this.poolLabel.poolId,
            strategyType: this.poolLabel.strategyType,
            coinType: this.poolLabel.asset.type,
            poolName: this.poolLabel.poolName,
            apr,
            tvl: {
                alphafi,
                parent,
            },
        };
    }
    /**
     * Calculate total value locked adjusted for leveraged position
     */
    async getTvl() {
        const coinType = this.poolLabel.asset.type;
        const decimals = await this.context.getCoinDecimals(coinType);
        const price = await this.context.getCoinPrice(coinType);
        const currentDebtToSupplyRatio = new Decimal(this.investorObject.currentDebtToSupplyRatio);
        const scaling = new Decimal(10).pow(20);
        const adjustedTokensInvested = new Decimal(this.poolObject.tokensInvested).mul(new Decimal(1).minus(currentDebtToSupplyRatio.div(scaling)));
        const tokenAmount = adjustedTokensInvested.div(new Decimal(10).pow(decimals));
        const usdValue = tokenAmount.mul(price);
        return { tokenAmount, usdValue };
    }
    /**
     * Calculate parent protocol TVL based on protocol type (Alphalend/Navi)
     */
    async getParentTvl() {
        const protocol = this.poolLabel.parentProtocol;
        const coinType = this.poolLabel.asset.type;
        const price = await this.context.getCoinPrice(coinType);
        if (protocol === 'Alphalend') {
            const tokenAmount = await this.context.getAlphaLendTvl(coinType);
            return { tokenAmount, usdValue: tokenAmount.mul(price) };
        }
        else if (protocol === 'Navi') {
            const tokenAmountUsd = await this.context.getNaviTvlByPoolId(this.poolLabel.poolId);
            return { tokenAmount: tokenAmountUsd.div(price), usdValue: tokenAmountUsd };
        }
        throw new Error(`Unsupported parent protocol: ${protocol}`);
    }
    /**
     * Calculate user's current pool balance from xToken balance
     * Includes leverage adjustment for single-asset position
     */
    async getBalance(_userAddress) {
        if (this.receiptObjects.length === 0 || this.receiptObjects[0].xTokenBalance === '0') {
            return { tokenAmount: new Decimal(0), usdValue: new Decimal(0) };
        }
        const xTokens = new Decimal(this.receiptObjects[0].xTokenBalance);
        const [exchangeRate, tokenDecimals, tokenPrice] = await Promise.all([
            Promise.resolve(this.exchangeRate()),
            this.context.getCoinDecimals(this.poolLabel.asset.type),
            this.context.getCoinPrice(this.poolLabel.asset.type),
        ]);
        const tokens = xTokens.mul(exchangeRate);
        const amount = tokens.div(new Decimal(10).pow(tokenDecimals));
        return { tokenAmount: amount, usdValue: amount.mul(tokenPrice) };
    }
    coinAmountToXToken(amount) {
        const exchangeRate = this.exchangeRate();
        return new Decimal(amount).div(exchangeRate).floor().toString();
    }
    /**
     * Parse pool object from blockchain response
     */
    parsePoolObject(response) {
        return this.safeParseObject(() => {
            const fields = this.extractFields(response);
            return {
                accRewardsPerXtoken: this.parseVecMap(fields.acc_rewards_per_xtoken || {}),
                depositFee: this.getStringField(fields, 'deposit_fee'),
                depositFeeMaxCap: this.getStringField(fields, 'deposit_fee_max_cap'),
                id: this.getStringField(fields, 'id'),
                imageUrl: this.getStringField(fields, 'image_url'),
                investorId: this.getStringField(fields, 'investor_id'),
                maxSupply: this.getStringField(fields, 'max_supply'),
                name: this.getStringField(fields, 'name'),
                paused: this.getBooleanField(fields, 'paused', false),
                rewards: (() => {
                    // Parse rewards PoolRewardsInfo { id, size }
                    const rewardsId = this.getNestedField(fields, 'rewards.id');
                    const rewardsSize = this.getNestedField(fields, 'rewards.size');
                    return { id: String(rewardsId), size: String(rewardsSize) };
                })(),
                tokensInvested: this.getStringField(fields, 'tokensInvested'),
                withdrawFeeMaxCap: this.getStringField(fields, 'withdraw_fee_max_cap'),
                withdrawalFee: this.getStringField(fields, 'withdrawal_fee'),
                xTokenSupply: this.getStringField(fields, 'xTokenSupply'),
            };
        }, 'Failed to parse SingleAssetLooping pool object');
    }
    /**
     * Parse investor object from blockchain response
     */
    parseInvestorObject(response) {
        return this.safeParseObject(() => {
            const fields = this.extractFields(response);
            return {
                assetLtv: this.getStringField(fields, 'asset_ltv'),
                curDebt: this.getStringField(fields, 'cur_debt'),
                currentDebtToSupplyRatio: this.getStringField(fields, 'current_debt_to_supply_ratio'),
                freeRewards: (() => {
                    const idVal = this.getNestedField(fields, 'free_rewards.id');
                    const sizeVal = this.getNestedField(fields, 'free_rewards.size');
                    return { id: String(idVal), size: String(sizeVal) };
                })(),
                id: this.getStringField(fields, 'id'),
                marketId: this.getStringField(fields, 'market_id'),
                maxCapPerformanceFee: this.getStringField(fields, 'max_cap_performance_fee'),
                minimumSwapAmount: this.getStringField(fields, 'minimum_swap_amount'),
                performanceFee: this.getStringField(fields, 'performance_fee'),
                positionCap: {
                    clientAddress: this.getNestedField(fields, 'position_cap.client_address'),
                    id: this.getNestedField(fields, 'position_cap.id'),
                    imageUrl: this.getNestedField(fields, 'position_cap.image_url'),
                    positionId: this.getNestedField(fields, 'position_cap.position_id'),
                },
                safeBorrowPercentage: this.getStringField(fields, 'safe_borrow_percentage'),
                tokensDeposited: this.getStringField(fields, 'tokensDeposited'),
            };
        }, 'Failed to parse SingleAssetLooping investor object');
    }
    /**
     * Parse parent pool object (not applicable for SingleAssetLooping)
     */
    parseParentPoolObject(_response) {
        throw new Error('SingleAssetLooping strategy does not have parent pool objects');
    }
    /**
     * Parse receipt objects from blockchain responses
     */
    parseReceiptObjects(responses) {
        return responses
            .map((response, index) => {
            return this.safeParseObject(() => {
                const fields = this.extractFields(response);
                return {
                    id: this.getStringField(fields, 'id'),
                    imageUrl: this.getStringField(fields, 'image_url'),
                    lastAccRewardPerXtoken: this.parseVecMap(fields.last_acc_reward_per_xtoken || {}),
                    name: this.getStringField(fields, 'name'),
                    owner: this.getStringField(fields, 'owner'),
                    pendingRewards: this.parseVecMap(fields.pending_rewards || {}),
                    poolId: this.getStringField(fields, 'pool_id'),
                    xTokenBalance: this.getStringField(fields, 'xtoken_balance') ||
                        this.getStringField(fields, 'xTokenBalance'),
                };
            }, `Failed to parse SingleAssetLooping receipt object at index ${index}`);
        })
            .filter((receipt) => receipt.poolId === this.poolLabel.poolId);
    }
    async updatePool(tx) {
        await this.collectAndSwapRewards(tx);
        tx.moveCall({
            target: `${this.poolLabel.packageId}::alphafi_alphalend_single_loop_pool::update_pool`,
            typeArguments: [this.poolLabel.asset.type],
            arguments: [
                tx.object(VERSIONS.ALPHALEND_VERSION),
                tx.object(this.poolLabel.poolId),
                tx.object(this.poolLabel.investorId),
                tx.object(DISTRIBUTOR_OBJECT_ID),
                tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                tx.object(CLOCK_PACKAGE_ID),
            ],
        });
        return tx;
    }
    async collectAndSwapRewards(tx) {
        const [alphaCoin, stsuiCoin, suiCoin, blueCoin, deepCoin, usdcCoin, walCoin, tbtcCoin, suibtcCoin, xaumCoin,] = await this.context.getCoinsBySymbols([
            'ALPHA',
            'stSUI',
            'SUI',
            'BLUE',
            'DEEP',
            'USDC',
            'WAL',
            'tBTC',
            'wBTC',
            'XAUm',
        ]);
        const coinTypes = [
            alphaCoin,
            stsuiCoin,
            suiCoin,
            blueCoin,
            deepCoin,
            usdcCoin,
            walCoin,
            tbtcCoin,
            suibtcCoin,
            xaumCoin,
        ].map((entry) => entry.coinType);
        const alphalendClient = this.context.alphalendClient;
        await alphalendClient.updatePrices(tx, [this.poolLabel.asset.type]);
        const positionId = this.investorObject.positionCap.positionId;
        const portfolio = await alphalendClient.getUserPortfolioFromPosition(positionId);
        const rewards = portfolio?.rewardsToClaim;
        if (!rewards) {
            console.log('no rewards for pool id: ', this.poolLabel.poolId);
            return;
        }
        console.log(rewards);
        for (const x of rewards) {
            if (x.coinType == alphaCoin.coinType) {
                tx.moveCall({
                    target: `${this.poolLabel.packageId}::alphafi_alphalend_single_loop_pool::collect_reward_and_swap_bluefin`,
                    typeArguments: [this.poolLabel.asset.type, alphaCoin.coinType, stsuiCoin.coinType],
                    arguments: [
                        tx.object(VERSIONS.ALPHALEND_VERSION),
                        tx.object(this.poolLabel.investorId),
                        tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                        tx.object(await this.context.getPoolIdBySymbolsAndProtocol('ALPHA', 'stSUI', 'bluefin')),
                        tx.object(GLOBAL_CONFIGS.BLUEFIN),
                        tx.pure.bool(true),
                        tx.pure.bool(false),
                        tx.object(CLOCK_PACKAGE_ID),
                    ],
                });
                tx.moveCall({
                    target: `${this.poolLabel.packageId}::alphafi_alphalend_single_loop_pool::collect_reward_and_swap_bluefin`,
                    typeArguments: [this.poolLabel.asset.type, stsuiCoin.coinType, suiCoin.coinType],
                    arguments: [
                        tx.object(VERSIONS.ALPHALEND_VERSION),
                        tx.object(this.poolLabel.investorId),
                        tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                        tx.object(await this.context.getPoolIdBySymbolsAndProtocol('stSUI', 'SUI', 'bluefin')),
                        tx.object(GLOBAL_CONFIGS.BLUEFIN),
                        tx.pure.bool(true),
                        tx.pure.bool(true),
                        tx.object(CLOCK_PACKAGE_ID),
                    ],
                });
            }
            else if (coinTypes.includes(x.coinType) && x.coinType != this.poolLabel.asset.type) {
                tx.moveCall({
                    target: `${this.poolLabel.packageId}::alphafi_alphalend_single_loop_pool::collect_reward_and_swap_bluefin`,
                    typeArguments: [this.poolLabel.asset.type, x.coinType, suiCoin.coinType],
                    arguments: [
                        tx.object(VERSIONS.ALPHALEND_VERSION),
                        tx.object(this.poolLabel.investorId),
                        tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                        tx.object(await this.context.getPoolIdByTypesAndProtocol(x.coinType, suiCoin.coinType, 'bluefin')),
                        tx.object(GLOBAL_CONFIGS.BLUEFIN),
                        tx.pure.bool(true),
                        tx.pure.bool(true),
                        tx.object(CLOCK_PACKAGE_ID),
                    ],
                });
            }
        }
        if (['tBTC', 'USDSUI', 'XAUm', 'WBTC'].includes(this.poolLabel.asset.name)) {
            tx.moveCall({
                target: `${this.poolLabel.packageId}::alphafi_alphalend_single_loop_pool::collect_reward_and_swap_bluefin`,
                typeArguments: [this.poolLabel.asset.type, suiCoin.coinType, usdcCoin.coinType],
                arguments: [
                    tx.object(VERSIONS.ALPHALEND_VERSION),
                    tx.object(this.poolLabel.investorId),
                    tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('SUI', 'USDC', 'bluefin')),
                    tx.object(GLOBAL_CONFIGS.BLUEFIN),
                    tx.pure.bool(true),
                    tx.pure.bool(true),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
            tx.moveCall({
                target: `${this.poolLabel.packageId}::alphafi_alphalend_single_loop_pool::collect_reward_and_swap_bluefin`,
                typeArguments: [this.poolLabel.asset.type, this.poolLabel.asset.type, usdcCoin.coinType],
                arguments: [
                    tx.object(VERSIONS.ALPHALEND_VERSION),
                    tx.object(this.poolLabel.investorId),
                    tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol(this.poolLabel.asset.name, 'USDC', 'bluefin')),
                    tx.object(GLOBAL_CONFIGS.BLUEFIN),
                    tx.pure.bool(false),
                    tx.pure.bool(true),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
        }
        else if (this.poolLabel.asset.name === 'wBTC') {
            tx.moveCall({
                target: `${this.poolLabel.packageId}::alphafi_alphalend_single_loop_pool::collect_reward_and_swap_bluefin`,
                typeArguments: [suibtcCoin.coinType, suiCoin.coinType, suibtcCoin.coinType],
                arguments: [
                    tx.object(VERSIONS.ALPHALEND_VERSION),
                    tx.object(this.poolLabel.investorId),
                    tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol('SUI', 'wBTC', 'bluefin')),
                    tx.object(GLOBAL_CONFIGS.BLUEFIN),
                    tx.pure.bool(true),
                    tx.pure.bool(true),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
        }
        else if (['DEEP', 'WAL'].includes(this.poolLabel.asset.name)) {
            tx.moveCall({
                target: `${this.poolLabel.packageId}::alphafi_alphalend_single_loop_pool::collect_reward_and_swap_bluefin`,
                typeArguments: [this.poolLabel.asset.type, this.poolLabel.asset.type, suiCoin.coinType],
                arguments: [
                    tx.object(VERSIONS.ALPHALEND_VERSION),
                    tx.object(this.poolLabel.investorId),
                    tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                    tx.object(await this.context.getPoolIdBySymbolsAndProtocol(this.poolLabel.asset.name, 'SUI', 'bluefin')),
                    tx.object(GLOBAL_CONFIGS.BLUEFIN),
                    tx.pure.bool(false),
                    tx.pure.bool(true),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
        }
    }
    async deposit(tx, options) {
        const alphalendClient = this.context.alphalendClient;
        // get Coin Object
        const depositCoin = this.context.blockchain.getCoinObject(tx, this.poolLabel.asset.type, options.address, options.amount);
        const receiptOption = this.context.blockchain.getOptionReceipt(tx, this.poolLabel.receipt.type, this.receiptObjects.length > 0 ? this.receiptObjects[0].id : undefined);
        await alphalendClient.updatePrices(tx, [this.poolLabel.asset.type]);
        await this.collectAndSwapRewards(tx);
        tx.moveCall({
            target: `${this.poolLabel.packageId}::alphafi_alphalend_single_loop_pool::user_deposit`,
            typeArguments: [this.poolLabel.asset.type],
            arguments: [
                tx.object(VERSIONS.ALPHALEND_VERSION),
                tx.object(VERSIONS.ALPHA_VERSIONS[1]),
                receiptOption,
                tx.object(this.poolLabel.poolId),
                depositCoin,
                tx.object(this.poolLabel.investorId),
                tx.object(DISTRIBUTOR_OBJECT_ID),
                tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                tx.object(CLOCK_PACKAGE_ID),
            ],
        });
    }
    async withdraw(tx, options) {
        if (this.receiptObjects.length === 0) {
            throw new Error('No receipt found');
        }
        const alphalendClient = this.context.alphalendClient;
        await alphalendClient.updatePrices(tx, [this.poolLabel.asset.type]);
        let xTokens = this.coinAmountToXToken(options.amount);
        if (options.withdrawMax) {
            xTokens = this.receiptObjects[0].xTokenBalance;
        }
        await this.collectAndSwapRewards(tx);
        const noneAlphaReceipt = tx.moveCall({
            target: `0x1::option::none`,
            typeArguments: [
                '0x9bbd650b8442abb082c20f3bc95a9434a8d47b4bef98b0832dab57c1a8ba7123::alphapool::Receipt',
            ],
            arguments: [],
        });
        const [coin] = tx.moveCall({
            target: `${this.poolLabel.packageId}::alphafi_alphalend_single_loop_pool::user_withdraw`,
            typeArguments: [this.poolLabel.asset.type],
            arguments: [
                tx.object(VERSIONS.ALPHALEND_VERSION),
                tx.object(VERSIONS.ALPHA_VERSIONS[1]),
                tx.object(this.receiptObjects[0].id),
                noneAlphaReceipt,
                tx.object(POOLS.ALPHA_LEGACY),
                tx.object(this.poolLabel.poolId),
                tx.pure.u64(xTokens),
                tx.object(this.poolLabel.investorId),
                tx.object(DISTRIBUTOR_OBJECT_ID),
                tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                tx.object(CLOCK_PACKAGE_ID),
            ],
        });
        this.context.blockchain.sendCoinToAddressBalance(tx, this.poolLabel.asset.type, options.address, coin);
    }
    async claimRewards(tx, alphaReceipt) {
        this.receiptObjects.forEach((receipt) => {
            alphaReceipt = tx.moveCall({
                target: `${this.poolLabel.packageId}::alphafi_alphalend_single_loop_pool::get_user_rewards_all`,
                typeArguments: [this.poolLabel.asset.type],
                arguments: [
                    tx.object(VERSIONS.ALPHALEND_VERSION),
                    tx.object(VERSIONS.ALPHA_VERSIONS[1]),
                    tx.object(receipt.id),
                    alphaReceipt,
                    tx.object(this.poolLabel.poolId),
                    tx.object(POOLS.ALPHA_LEGACY),
                    tx.object(DISTRIBUTOR_OBJECT_ID),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
        });
    }
}
//# sourceMappingURL=singleAssetLooping.js.map